import { useEffect } from 'react'
import { Trophy } from 'lucide-react'
import { T } from '../theme'

/**
 * LevelSelector — sélecteur de niveau de championnat.
 *
 * Le mapping LEVELS_BY_CATEGORY suit la nomenclature FFF :
 * - Seniors : N1, N2, N3, R1, R2, R3, D1, D2, D3, D4
 * - U17, U19 : niveau national en plus (championnat structuré)
 * - U14 à U18 : niveaux régionaux/départementaux uniquement
 * - Catégories sans championnat structuré (U13 et moins, Vétérans, Féminines) :
 *   pas de sélecteur — retourne null pour level
 *
 * Utilisé dans :
 * - SeasonAssistant (étape 1 — préparer la saison à venir)
 * - Benchmark (mini-form pour activer le comparatif sur la saison en cours)
 * - À terme : CoachSettings si on veut éditer a posteriori
 */

export const SENIOR_LEVEL_LABELS = {
  L2: 'Ligue 2',
  L3: 'Ligue 3',
  N:  'National',
  N1: 'National 1',
  N2: 'National 2',
  R1: 'Régional 1',
  R2: 'Régional 2',
  R3: 'Régional 3',
  D1: 'Départemental 1',
  D2: 'Départemental 2',
  D3: 'Départemental 3',
}

export const LEVELS_BY_CATEGORY = {
  'Seniors':  ['N1', 'N2', 'N3', 'R1', 'R2', 'R3', 'D1', 'D2', 'D3', 'D4'],
  'U19':      ['U19 Nationaux', 'R1', 'R2', 'D1', 'D2', 'D3'],
  'U18':      ['R1', 'R2', 'D1', 'D2', 'D3'],
  'U17':      ['U17 Nationaux', 'R1', 'R2', 'D1', 'D2', 'D3'],
  'U16':      ['R1', 'R2', 'D1', 'D2', 'D3'],
  'U15':      ['R1', 'R2', 'D1', 'D2', 'D3'],
  'U14':      ['R1', 'R2', 'D1', 'D2', 'D3'],
  // Pas de niveau structuré pour U13 et moins (foot animation/pré-formation),
  // ni pour Vétérans (foot loisir) ni Féminines (variation libre selon district).
}

/** True si la catégorie a une liste de niveaux à proposer. */
export function categoryHasLevels(category) {
  return Boolean(category && LEVELS_BY_CATEGORY[category]?.length)
}

/** Retourne la liste des niveaux pour une catégorie, ou [] si non applicable. */
export function getLevelsForCategory(category) {
  return LEVELS_BY_CATEGORY[category] || []
}

export default function LevelSelector({
  category,
  value,
  onChange,
  required = false,
  size = 'normal', // 'compact' | 'normal'
  hint = true,
}) {
  const levels = getLevelsForCategory(category)
  const hasLevels = levels.length > 0

  // Si la catégorie change et que le niveau actuel n'est plus valide, on reset
  useEffect(() => {
    if (value && !levels.includes(value)) {
      onChange(null)
    }
  }, [category, value, levels, onChange])

  // Si la catégorie n'a pas de niveau structuré, message discret
  if (!hasLevels) {
    return (
      <div style={{
        padding: '12px 14px',
        background: T.bg,
        border: `1px solid ${T.rule}`,
        fontFamily: T.mono, fontSize: 10, letterSpacing: '.04em',
        color: T.muted, lineHeight: 1.5,
      }}>
        Pas de niveau structuré pour cette catégorie. Le comparatif ne s'applique pas ici.
      </div>
    )
  }

  const btnPad = size === 'compact' ? '8px 10px' : '10px 12px'
  const btnFontSize = size === 'compact' ? 9 : 10

  return (
    <div>
      <label style={{
        display: 'block', fontFamily: T.mono, fontSize: 9,
        letterSpacing: '.14em', textTransform: 'uppercase', color: T.muted, marginBottom: 8,
      }}>
        <Trophy size={12} style={{ display: 'inline', marginRight: 6, verticalAlign: '-2px' }} />
        Niveau de championnat
        {required && <span style={{ marginLeft: 6, color: T.gold }}>*</span>}
      </label>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
        gap: 6,
      }}>
        {levels.map(lvl => {
          const isActive = value === lvl
          const isNational = lvl.includes('Nationaux') || lvl.startsWith('N')
          return (
            <button
              key={lvl}
              type="button"
              onClick={() => onChange(lvl)}
              style={{
                padding: btnPad,
                background: isActive ? T.gold : T.surface,
                border: `1px solid ${isActive ? T.gold : T.rule}`,
                color: isActive ? T.ink : T.muted,
                fontFamily: T.mono, fontSize: btnFontSize, letterSpacing: '.06em',
                cursor: 'pointer', fontWeight: isActive ? 700 : 400,
                transition: 'all .12s',
                position: 'relative',
              }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.borderColor = T.goldBdr }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.borderColor = T.rule }}
            >
              {SENIOR_LEVEL_LABELS[lvl] ?? lvl}
              {isNational && !isActive && (
                <span style={{
                  position: 'absolute', top: 2, right: 4,
                  fontSize: 6, color: T.gold, letterSpacing: '.05em',
                }}>★</span>
              )}
            </button>
          )
        })}
      </div>

      {hint && (
        <p style={{
          fontSize: 11, color: T.muted, marginTop: 8,
          fontFamily: T.mono, letterSpacing: '.04em', lineHeight: 1.5,
        }}>
          Utilisé pour te comparer aux clubs du même niveau dans le comparatif national.
        </p>
      )}
    </div>
  )
}
